import React, {
    useMemo,
    useState
} from "react"
import {
    Box,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    MenuItem,
    Chip,
    Button,
    Avatar,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery
} from "@mui/material"
import {
    SearchOutlined,
    EventOutlined,
    EditCalendarOutlined,
    CloseOutlined,
    RefreshOutlined,
    ApartmentOutlined,
    PeopleAltOutlined,
    AddOutlined,
    DeleteOutlineOutlined,
    WarningAmberOutlined
} from "@mui/icons-material"
import { Message, toaster } from "rsuite"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import _ from "lodash"
import {
    useGetClientListQuery,
    useUpdateClientExpiryMutation,
    useGetClientDeletePreviewQuery,
    useDeleteClientMutation
} from "../Redux/actions/setting.action"

const DATE_FORMAT = "DD MMM YYYY"
const API_DATE_FORMAT = "YYYY-MM-DD"

const STATUS_META = {
    active: { label: "Active", color: "#047857", bg: "#ecfdf5" },
    expiring: { label: "Expiring soon", color: "#b45309", bg: "#fffbeb" },
    expired: { label: "Expired", color: "#b91c1c", bg: "#fef2f2" },
    no_expiry: { label: "No expiry", color: "#475569", bg: "#f1f5f9" }
}

const FILTERS = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "expiring", label: "Expiring soon" },
    { key: "expired", label: "Expired" },
    { key: "no_expiry", label: "No expiry" }
]

const SORTS = {
    newest: {
        label: "Newest joined",
        compare: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") || b.id - a.id
    },
    oldest: {
        label: "Oldest joined",
        compare: (a, b) => (a.created_at || "").localeCompare(b.created_at || "") || a.id - b.id
    },
    expiry: {
        // Clients without an expiry go last
        label: "Expiry date",
        compare: (a, b) => (a.expiry_date || "9999").localeCompare(b.expiry_date || "9999")
    },
    name: {
        label: "Name A–Z",
        compare: (a, b) => a.name.localeCompare(b.name)
    }
}

const QUICK_EXTENDS = [
    { label: "+1 month", amount: 1, unit: "months" },
    { label: "+3 months", amount: 3, unit: "months" },
    { label: "+6 months", amount: 6, unit: "months" },
    { label: "+1 year", amount: 1, unit: "years" }
]

/* =========================
   HELPERS
========================= */

const isImageUrl = (logo) =>
    typeof logo === "string" && /^(https?:|data:image\/)/.test(logo)

const initialsOf = (name = "") =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()

const statusForDate = (expiryDate, expiringSoonDays) => {
    if (!expiryDate) return "no_expiry"
    const daysLeft = moment(expiryDate, API_DATE_FORMAT).diff(moment().startOf("day"), "days")
    if (daysLeft < 0) return "expired"
    if (daysLeft <= expiringSoonDays) return "expiring"
    return "active"
}

const describeExpiry = (expiryDate) => {
    if (!expiryDate) return "No end date set"
    const daysLeft = moment(expiryDate, API_DATE_FORMAT).diff(moment().startOf("day"), "days")
    if (daysLeft === 0) return "Expires today"
    if (daysLeft === 1) return "Expires tomorrow"
    if (daysLeft > 1) return `${daysLeft} days left`
    if (daysLeft === -1) return "Expired yesterday"
    return `Expired ${-daysLeft} days ago`
}

/* =========================
   SMALL COMPONENTS
========================= */

function StatusChip({ status }) {
    const meta = STATUS_META[status] || STATUS_META.no_expiry
    return (
        <Chip
            size="small"
            label={meta.label}
            sx={{
                color: meta.color,
                bgcolor: meta.bg,
                fontWeight: 600,
                fontSize: 12,
                borderRadius: "8px",
                "&::before": {
                    content: '""',
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: meta.color,
                    ml: 1
                }
            }}
        />
    )
}

function ClientIdentity({ client }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Avatar
                src={isImageUrl(client.logo) ? client.logo : undefined}
                variant="rounded"
                sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    bgcolor: "var(--color-primary-light)",
                    color: "var(--color-primary)",
                    fontWeight: 700,
                    fontSize: 14
                }}
            >
                {initialsOf(client.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }} noWrap>
                    {client.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "var(--text-light)" }} noWrap>
                    {[client.district, client.state].filter(Boolean).join(", ")}
                    {client.pin ? ` · ${client.pin}` : ""}
                </Typography>
            </Box>
        </Box>
    )
}

function JoinedDate({ createdAt }) {
    if (!createdAt) {
        return (
            <Tooltip title="This client was onboarded before join dates were recorded">
                <Typography sx={{ fontSize: 13, color: "var(--text-light)" }}>
                    Not recorded
                </Typography>
            </Tooltip>
        )
    }
    const joined = moment(createdAt)
    return (
        <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
                {joined.format(DATE_FORMAT)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "var(--text-light)" }}>
                {joined.fromNow()}
            </Typography>
        </Box>
    )
}

function ExpiryDate({ expiryDate, status }) {
    const meta = STATUS_META[status] || STATUS_META.no_expiry
    return (
        <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
                {expiryDate ? moment(expiryDate, API_DATE_FORMAT).format(DATE_FORMAT) : "—"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: expiryDate ? meta.color : "var(--text-light)" }}>
                {describeExpiry(expiryDate)}
            </Typography>
        </Box>
    )
}

/* =========================
   EXPIRY DIALOG
========================= */

function ExpiryDialog({ client, expiringSoonDays, onClose }) {
    const [expiryDate, setExpiryDate] = useState(client.expiry_date || "")
    const [updateExpiry, updateState] = useUpdateClientExpiryMutation()

    const unchanged = (expiryDate || null) === (client.expiry_date || null)
    const previewStatus = statusForDate(expiryDate || null, expiringSoonDays)

    const extendBy = (amount, unit) => {
        // Extend from the later of today and the current expiry, so renewing
        // an active client never shortens their remaining time
        const today = moment().startOf("day")
        const current = expiryDate ? moment(expiryDate, API_DATE_FORMAT) : null
        const base = current && current.isAfter(today) ? current : today
        setExpiryDate(base.clone().add(amount, unit).format(API_DATE_FORMAT))
    }

    const handleSave = async () => {
        try {
            const result = await updateExpiry({
                client_id: client.id,
                expiry_date: expiryDate || null
            }).unwrap()
            toaster.push(
                <Message showIcon type="success">
                    {_.get(result, "message", "Expiry date updated")}
                </Message>,
                { placement: "topCenter" }
            )
            onClose()
        } catch (error) {
            toaster.push(
                <Message showIcon type="error">
                    {_.isString(_.get(error, "data.detail"))
                        ? error.data.detail
                        : "Could not update the expiry date. Please try again."}
                </Message>,
                { placement: "topCenter" }
            )
        }
    }

    return (
        <Dialog
            open
            onClose={updateState.isLoading ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: "18px" } }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, pb: 1 }}>
                <Box>
                    <Typography sx={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 20 }}>
                        Update expiry date
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "var(--text-secondary)" }}>
                        {client.name}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={updateState.isLoading} aria-label="Close">
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "8px !important" }}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.5,
                    mb: 2.5,
                    borderRadius: "12px",
                    bgcolor: "#f8fafc"
                }}>
                    <Box>
                        <Typography sx={{ fontSize: 12, color: "var(--text-light)" }}>Current expiry</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {client.expiry_date
                                ? moment(client.expiry_date, API_DATE_FORMAT).format(DATE_FORMAT)
                                : "No expiry"}
                        </Typography>
                    </Box>
                    <StatusChip status={client.status} />
                </Box>

                <TextField
                    label="New expiry date"
                    type="date"
                    fullWidth
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EventOutlined fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />

                <Typography sx={{ fontSize: 12, color: "var(--text-light)", mt: 2, mb: 1 }}>
                    Quick extend
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {QUICK_EXTENDS.map((option) => (
                        <Chip
                            key={option.label}
                            label={option.label}
                            onClick={() => extendBy(option.amount, option.unit)}
                            variant="outlined"
                            sx={{
                                borderRadius: "8px",
                                fontWeight: 600,
                                borderColor: "var(--border-color)",
                                "&:hover": { borderColor: "var(--color-primary)", color: "var(--color-primary)" }
                            }}
                        />
                    ))}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontSize: 12, color: "var(--text-light)" }}>After saving:</Typography>
                        <StatusChip status={previewStatus} />
                    </Box>
                    {expiryDate && (
                        <Button
                            size="small"
                            onClick={() => setExpiryDate("")}
                            sx={{ textTransform: "none", color: "var(--color-danger)", fontWeight: 600 }}
                        >
                            Remove expiry
                        </Button>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={updateState.isLoading}
                    sx={{ textTransform: "none", color: "var(--text-secondary)", fontWeight: 600 }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={unchanged || updateState.isLoading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "10px",
                        px: 2.5,
                        bgcolor: "var(--color-primary)",
                        boxShadow: "none",
                        "&:hover": { bgcolor: "var(--color-primary-hover)", boxShadow: "none" }
                    }}
                >
                    {updateState.isLoading ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/* =========================
   DELETE DIALOG
========================= */

function DeleteDialog({ client, onClose }) {
    const [confirmName, setConfirmName] = useState("")
    const preview = useGetClientDeletePreviewQuery(client.id)
    const [deleteClient, deleteState] = useDeleteClientMutation()

    const counts = _.get(preview, "data.counts", [])
    const nameMatches = confirmName.trim() === client.name.trim()
    const canDelete = preview.isSuccess && nameMatches && !deleteState.isLoading

    const handleDelete = async () => {
        try {
            const result = await deleteClient({
                client_id: client.id,
                confirm_name: confirmName.trim()
            }).unwrap()
            toaster.push(
                <Message showIcon type="success">
                    {_.get(result, "message", "Client deleted")}
                </Message>,
                { placement: "topCenter" }
            )
            onClose()
        } catch (error) {
            toaster.push(
                <Message showIcon type="error">
                    {_.isString(_.get(error, "data.detail"))
                        ? error.data.detail
                        : "Could not delete the client. Please try again."}
                </Message>,
                { placement: "topCenter" }
            )
        }
    }

    return (
        <Dialog
            open
            onClose={deleteState.isLoading ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: "18px" } }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, pb: 1 }}>
                <Box>
                    <Typography sx={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 20 }}>
                        Delete client
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "var(--text-secondary)" }}>
                        {client.name}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={deleteState.isLoading} aria-label="Close">
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "8px !important" }}>
                <Box sx={{
                    display: "flex",
                    gap: 1.5,
                    p: 1.5,
                    mb: 2,
                    borderRadius: "12px",
                    bgcolor: "var(--color-danger-light)",
                    color: "#991b1b"
                }}>
                    <WarningAmberOutlined fontSize="small" sx={{ mt: "2px" }} />
                    <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
                        This permanently deletes the client, its user accounts and all of
                        its school data. <strong>This cannot be undone.</strong>
                    </Typography>
                </Box>

                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--text-light)", textTransform: "uppercase", mb: 1 }}>
                    What will be deleted
                </Typography>

                {preview.isLoading || preview.isFetching ? (
                    <Box sx={{ mb: 2 }}>
                        {[0, 1, 2].map((row) => <Skeleton key={row} height={28} />)}
                    </Box>
                ) : preview.isError ? (
                    <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Typography sx={{ fontSize: 13, color: "var(--color-danger)" }}>
                            {_.isString(_.get(preview, "error.data.detail"))
                                ? preview.error.data.detail
                                : "Couldn't check this client's data."}
                        </Typography>
                        <Button size="small" onClick={preview.refetch} sx={{ textTransform: "none" }}>
                            Retry
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{
                        mb: 2,
                        borderRadius: "12px",
                        border: "1px solid var(--border-color)",
                        maxHeight: 200,
                        overflowY: "auto"
                    }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.5, py: 1, borderBottom: "1px solid #f1f5f9" }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Client record</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>1</Typography>
                        </Box>
                        {counts.map((item) => (
                            <Box
                                key={item.label}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    px: 1.5,
                                    py: 1,
                                    borderBottom: "1px solid #f1f5f9",
                                    "&:last-child": { borderBottom: "none" }
                                }}
                            >
                                <Typography sx={{ fontSize: 13, color: "var(--text-secondary)" }}>{item.label}</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{item.count}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", mb: 1 }}>
                    Type <strong>{client.name}</strong> to confirm
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={client.name}
                    disabled={deleteState.isLoading}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && canDelete) handleDelete()
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={deleteState.isLoading}
                    sx={{ textTransform: "none", color: "var(--text-secondary)", fontWeight: 600 }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                    disabled={!canDelete}
                    startIcon={<DeleteOutlineOutlined />}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 2.5, boxShadow: "none" }}
                >
                    {deleteState.isLoading ? "Deleting…" : "Delete permanently"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/* =========================
   PAGE
========================= */

export default function ClientList() {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("md"))
    const navigate = useNavigate()

    const { data, isLoading, isFetching, isError, error, refetch } = useGetClientListQuery()

    const [search, setSearch] = useState("")
    const [filter, setFilter] = useState("all")
    const [sort, setSort] = useState("newest")
    const [editingClient, setEditingClient] = useState(null)
    const [deletingClient, setDeletingClient] = useState(null)

    const clients = useMemo(() => _.get(data, "list", []), [data])
    const expiringSoonDays = _.get(data, "expiring_soon_days", 30)

    const counts = useMemo(() => {
        const byStatus = _.countBy(clients, "status")
        return { all: clients.length, ...byStatus }
    }, [clients])

    const visibleClients = useMemo(() => {
        const query = search.trim().toLowerCase()
        return clients
            .filter((client) => filter === "all" || client.status === filter)
            .filter((client) => {
                if (!query) return true
                return [
                    client.name,
                    client.district,
                    client.state,
                    client.pin,
                    _.get(client, "admin.name"),
                    _.get(client, "admin.email")
                ].some((value) => value && String(value).toLowerCase().includes(query))
            })
            .sort(SORTS[sort].compare)
    }, [clients, search, filter, sort])

    /* ---------- error ---------- */

    if (isError) {
        const detail = _.get(error, "data.detail")
        return (
            <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "16px", border: "1px solid var(--border-color)" }}>
                <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
                    Couldn't load clients
                </Typography>
                <Typography sx={{ color: "var(--text-secondary)", fontSize: 14, mb: 3 }}>
                    {_.isString(detail) ? detail : "Check that the server is running, then try again."}
                </Typography>
                <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={refetch} sx={{ textTransform: "none" }}>
                    Try again
                </Button>
            </Paper>
        )
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* ================= SUMMARY ================= */}

            <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                gap: 2
            }}>
                {[
                    { key: "all", label: "Total clients", icon: <ApartmentOutlined />, color: "var(--color-primary)", bg: "var(--color-primary-light)" },
                    { key: "active", ...STATUS_META.active },
                    { key: "expiring", ...STATUS_META.expiring, label: `Expiring in ${expiringSoonDays} days` },
                    { key: "expired", ...STATUS_META.expired }
                ].map((card) => (
                    <Paper
                        key={card.key}
                        elevation={0}
                        component="button"
                        type="button"
                        onClick={() => setFilter(card.key)}
                        sx={{
                            p: 2,
                            textAlign: "left",
                            cursor: "pointer",
                            borderRadius: "14px",
                            border: "1px solid",
                            borderColor: filter === card.key ? card.color : "var(--border-color)",
                            boxShadow: filter === card.key ? `0 0 0 3px ${card.bg}` : "none",
                            bgcolor: "var(--bg-card)",
                            fontFamily: "inherit",
                            transition: "all .15s ease",
                            "&:hover": { borderColor: card.color }
                        }}
                    >
                        <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 }}>
                            {card.label}
                        </Typography>
                        {isLoading ? (
                            <Skeleton width={40} height={36} />
                        ) : (
                            <Typography sx={{ fontSize: 28, fontWeight: 700, color: card.color, fontFamily: "var(--font-heading)" }}>
                                {counts[card.key] || 0}
                            </Typography>
                        )}
                    </Paper>
                ))}
            </Box>

            {/* ================= LIST ================= */}

            <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid var(--border-color)", overflow: "hidden" }}>

                {/* Toolbar */}
                <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    gap: 1.5,
                    p: 2,
                    borderBottom: "1px solid var(--border-color)"
                }}>
                    <TextField
                        size="small"
                        placeholder="Search by name, location or admin email"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#f8fafc" } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <TextField
                            select
                            size="small"
                            label="Sort by"
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            sx={{ minWidth: 170, flex: { xs: 1, md: "none" }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        >
                            {Object.entries(SORTS).map(([key, option]) => (
                                <MenuItem key={key} value={key}>{option.label}</MenuItem>
                            ))}
                        </TextField>
                        <Tooltip title="Refresh">
                            <span>
                                <IconButton
                                    onClick={refetch}
                                    disabled={isFetching}
                                    sx={{ border: "1px solid var(--border-color)", borderRadius: "10px" }}
                                    aria-label="Refresh client list"
                                >
                                    <RefreshOutlined fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Status filter */}
                <Box sx={{ display: "flex", gap: 1, px: 2, py: 1.5, overflowX: "auto", borderBottom: "1px solid var(--border-color)" }}>
                    {FILTERS.map((option) => {
                        const selected = filter === option.key
                        return (
                            <Chip
                                key={option.key}
                                label={`${option.label} (${counts[option.key] || 0})`}
                                onClick={() => setFilter(option.key)}
                                sx={{
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    fontSize: 12,
                                    flexShrink: 0,
                                    bgcolor: selected ? "var(--color-primary)" : "#f1f5f9",
                                    color: selected ? "#fff" : "var(--text-secondary)",
                                    "&:hover": { bgcolor: selected ? "var(--color-primary-hover)" : "#e2e8f0" }
                                }}
                            />
                        )
                    })}
                </Box>

                {/* Body */}
                {isLoading ? (
                    <Box sx={{ p: 2 }}>
                        {[0, 1, 2, 3].map((row) => (
                            <Skeleton key={row} height={64} sx={{ borderRadius: "10px" }} />
                        ))}
                    </Box>
                ) : visibleClients.length === 0 ? (
                    <Box sx={{ py: 7, px: 3, textAlign: "center" }}>
                        <ApartmentOutlined sx={{ fontSize: 40, color: "var(--text-light)", mb: 1 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                            {clients.length === 0 ? "No clients yet" : "No clients match"}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", mb: 2.5 }}>
                            {clients.length === 0
                                ? "Onboard your first school or college to see it here."
                                : "Try a different search or status filter."}
                        </Typography>
                        {clients.length === 0 ? (
                            <Button
                                variant="contained"
                                startIcon={<AddOutlined />}
                                onClick={() => navigate("/admin/new")}
                                sx={{ textTransform: "none", borderRadius: "10px", bgcolor: "var(--color-primary)", boxShadow: "none" }}
                            >
                                Create new client
                            </Button>
                        ) : (
                            <Button
                                onClick={() => { setSearch(""); setFilter("all") }}
                                sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                                Clear filters
                            </Button>
                        )}
                    </Box>
                ) : isMobile ? (

                    /* Mobile cards */
                    <Box>
                        {visibleClients.map((client) => (
                            <Box key={client.id} sx={{ p: 2, borderBottom: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 1.5 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                    <ClientIdentity client={client} />
                                    <StatusChip status={client.status} />
                                </Box>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600 }}>Joined</Typography>
                                        <JoinedDate createdAt={client.created_at} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600 }}>Expiry</Typography>
                                        <ExpiryDate expiryDate={client.expiry_date} status={client.status} />
                                    </Box>
                                </Box>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<EditCalendarOutlined />}
                                        onClick={() => setEditingClient(client)}
                                        sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}
                                    >
                                        Update expiry
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setDeletingClient(client)}
                                        aria-label={`Delete ${client.name}`}
                                        sx={{ borderRadius: "10px", minWidth: 48 }}
                                    >
                                        <DeleteOutlineOutlined fontSize="small" />
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (

                    /* Desktop table */
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    "& th": {
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "var(--text-light)",
                                        textTransform: "uppercase",
                                        letterSpacing: ".04em",
                                        bgcolor: "#f8fafc",
                                        borderBottom: "1px solid var(--border-color)"
                                    }
                                }}>
                                    <TableCell>Client</TableCell>
                                    <TableCell>Admin</TableCell>
                                    <TableCell align="center">Users</TableCell>
                                    <TableCell>Joined</TableCell>
                                    <TableCell>Expiry</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleClients.map((client) => (
                                    <TableRow
                                        key={client.id}
                                        hover
                                        sx={{ "& td": { borderBottom: "1px solid #f1f5f9" }, "&:last-child td": { borderBottom: "none" } }}
                                    >
                                        <TableCell sx={{ maxWidth: 280 }}>
                                            <ClientIdentity client={client} />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 220 }}>
                                            {client.admin ? (
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 500 }} noWrap>
                                                        {client.admin.name || "—"}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 12, color: "var(--text-light)" }} noWrap>
                                                        {client.admin.email}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography sx={{ fontSize: 13, color: "var(--text-light)" }}>
                                                    No admin
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "var(--text-secondary)", fontSize: 13 }}>
                                                <PeopleAltOutlined sx={{ fontSize: 16 }} />
                                                {client.user_count}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <JoinedDate createdAt={client.created_at} />
                                        </TableCell>
                                        <TableCell>
                                            <ExpiryDate expiryDate={client.expiry_date} status={client.status} />
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={client.status} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EditCalendarOutlined fontSize="small" />}
                                                onClick={() => setEditingClient(client)}
                                                sx={{
                                                    textTransform: "none",
                                                    borderRadius: "8px",
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap",
                                                    borderColor: "var(--border-color)",
                                                    color: "var(--text-secondary)",
                                                    "&:hover": { borderColor: "var(--color-primary)", color: "var(--color-primary)", bgcolor: "var(--color-primary-light)" }
                                                }}
                                            >
                                                Update expiry
                                            </Button>
                                            <Tooltip title="Delete client">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeletingClient(client)}
                                                    aria-label={`Delete ${client.name}`}
                                                    sx={{
                                                        ml: 1,
                                                        border: "1px solid var(--border-color)",
                                                        borderRadius: "8px",
                                                        color: "var(--text-light)",
                                                        "&:hover": {
                                                            color: "var(--color-danger)",
                                                            borderColor: "var(--color-danger)",
                                                            bgcolor: "var(--color-danger-light)"
                                                        }
                                                    }}
                                                >
                                                    <DeleteOutlineOutlined fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {editingClient && (
                <ExpiryDialog
                    key={editingClient.id}
                    client={editingClient}
                    expiringSoonDays={expiringSoonDays}
                    onClose={() => setEditingClient(null)}
                />
            )}

            {deletingClient && (
                <DeleteDialog
                    key={deletingClient.id}
                    client={deletingClient}
                    onClose={() => setDeletingClient(null)}
                />
            )}
        </Box>
    )
}
